import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import toast from 'react-hot-toast';
import StatusBadge, { STATUS_CONFIG } from './StatusBadge';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

// Mirroring backend STATUS_TRANSITIONS in claimModel.js
const STATUS_TRANSITIONS = {
  reported: ['open', 'denied', 'closed'],
  new: ['open', 'denied', 'closed'], // alias for backward compat
  open: ['review', 'investigation', 'approved', 'denied', 'closed'],
  review: ['open', 'investigation', 'approved', 'denied', 'closed'],
  investigation: ['review', 'evaluation', 'negotiation', 'approved', 'denied', 'closed'],
  evaluation: ['investigation', 'negotiation', 'approved', 'denied', 'closed'],
  negotiation: ['investigation', 'evaluation', 'approved', 'denied', 'closed'],
  approved: ['closed', 'reopened'],
  denied: ['reopened', 'closed'],
  closed: ['reopened'],
  reopened: ['open', 'review', 'investigation'],
};

const STATUS_LABEL_KEYS = {
  reported: 'screens.claimsScreen.statusReported',
  new: 'screens.claimsScreen.statusReported',
  open: 'screens.claimsScreen.statusOpen',
  review: 'screens.claimsScreen.statusReview',
  investigation: 'screens.claimsScreen.statusInvestigation',
  evaluation: 'screens.claimsScreen.statusEvaluation',
  negotiation: 'screens.claimsScreen.statusNegotiation',
  approved: 'screens.claimsScreen.statusApproved',
  denied: 'screens.claimsScreen.statusDenied',
  closed: 'screens.claimsScreen.statusClosed',
  reopened: 'screens.claimsScreen.statusReopened',
};

// These statuses require a mandatory reason
const REASON_REQUIRED = ['denied', 'closed'];

const ChangeStatusModal = ({ claim, onClose, onSuccess, S }) => {
  const { t } = useTranslation();
  const nextStatuses = STATUS_TRANSITIONS[claim.status] || [];
  const [target, setTarget] = useState(nextStatuses[0] || '');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const requiresReason = REASON_REQUIRED.includes(target);

  const handleSubmit = async () => {
    if (!target) return;
    if (requiresReason && !reason.trim()) {
      toast.error(t('screens.claimsScreen.detail.reasonRequiredError'));
      return;
    }
    const claimId = claim._id;
    if (!claimId) {
      toast.error(t('screens.claimsScreen.detail.notFullyLoaded'));
      return;
    }
    setLoading(true);
    try {
      await axios.patch(`${BASE_URL}/claims/${claimId}/status`, { status: target, ...(reason.trim() ? { reason: reason.trim() } : {}) }, { withCredentials: true });
      const statusLabel = STATUS_LABEL_KEYS[target] ? t(STATUS_LABEL_KEYS[target]) : (STATUS_CONFIG[target]?.label || target);
      toast.success(t('screens.claimsScreen.detail.statusChanged', { status: statusLabel }));
      onSuccess(target);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.msg || err.message || t('screens.claimsScreen.detail.statusChangeFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 460, boxShadow: '0 24px 60px rgba(0,0,0,0.18)' }}>
        <div style={{ fontSize: 17, fontWeight: 700, color: S.textPrimary, marginBottom: 4 }}>{t('screens.claimsScreen.detail.changeStatusTitle')}</div>
        <div style={{ fontSize: 13, color: S.textMuted, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
          {t('screens.claimsScreen.detail.currentStatus')} <StatusBadge status={claim.status} />
        </div>

        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10 }}>{t('screens.claimsScreen.detail.selectNewStatus')}</div>
          {nextStatuses.length === 0 ? (
            <div style={{ fontSize: 13, color: S.textMuted, fontStyle: 'italic' }}>{t('screens.claimsScreen.detail.noValidTransitions')}</div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {nextStatuses.map(s => {
                const cfg = STATUS_CONFIG[s] || {};
                const label = STATUS_LABEL_KEYS[s] ? t(STATUS_LABEL_KEYS[s]) : (cfg.label || s);
                return (
                  <button
                    key={s}
                    onClick={() => setTarget(s)}
                    style={{
                      padding: '8px 18px',
                      borderRadius: 20,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                      border: `2px solid ${target === s ? cfg.color : S.borderLight}`,
                      background: target === s ? cfg.bg : '#fff',
                      color: target === s ? cfg.color : S.textSecondary,
                      transition: 'all 0.15s',
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ marginBottom: 22 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6 }}>
            {requiresReason ? t('screens.claimsScreen.detail.reasonRequired') : t('screens.claimsScreen.detail.reasonOptional')}
          </div>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder={requiresReason ? t('screens.claimsScreen.detail.reasonPlaceholder') : t('screens.claimsScreen.detail.reasonOptionalPlaceholder')}
            rows={3}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: `1px solid ${requiresReason && !reason.trim() ? '#fca5a5' : S.borderLight}`,
              borderRadius: 8,
              fontSize: 13,
              color: S.textPrimary,
              resize: 'vertical',
              outline: 'none',
              fontFamily: 'inherit',
              boxSizing: 'border-box',
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 22px',
              background: '#fff',
              border: `1px solid ${S.borderLight}`,
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              color: S.textSecondary,
            }}
          >
            {t('screens.claimsScreen.detail.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={!target || loading}
            style={{
              padding: '10px 22px',
              background: `linear-gradient(135deg, ${S.burgundy}, ${S.burgundy2})`,
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              cursor: target && !loading ? 'pointer' : 'not-allowed',
              opacity: target && !loading ? 1 : 0.55,
              transition: 'opacity 0.15s',
            }}
          >
            {loading ? t('screens.claimsScreen.detail.saving') : t('screens.claimsScreen.detail.confirmChange')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChangeStatusModal;
